/**
 * The pill over the composer while a send's attachments go up (chat-plan.md
 * D398): its title, its numbers and its bar, read out of the send's
 * `UploadState`. The desktop's `state/upload.rs`, rule for rule.
 *
 * The numbers are the sizes of the files the sender *picked*, not the bytes on
 * the wire: a photograph goes up as a few hundred kilobytes of WebP (D233),
 * and how many is unknown until its encode is over. A total that fell from
 * 8 MB to 0,3 MB halfway through would read as a fault, and «0,3 МБ» under a
 * 4 MB photo reads as the wrong file.
 */
import type { FileKind } from "../gen/dto";
import type { UploadKind, UploadState } from "../store/types";
import type { Dict } from "./i18n";

/** What a kind on its way out counts as, by the kind it travels as — `null`
 * for a voice note, which never raises the pill: nobody picked it, it is tens
 * of kilobytes, and a pill flashing over every note would be noise. */
export function uploadKindOf(kind: FileKind): UploadKind | null {
  switch (kind) {
    case "photo":
      return "photo";
    case "video":
    case "video_message":
      return "video";
    case "voice":
      return null;
    default:
      return "file";
  }
}

/** The pill for a send of these attachments, as `[send kind, bytes]` — or
 * `null` when there is nothing for it to show: no attachments, or a voice
 * note, which never shares a send with anything else. */
export function newUpload(
  channelId: number,
  nonce: string,
  attachments: ReadonlyArray<readonly [FileKind, number]>,
): UploadState | null {
  if (attachments.length === 0) return null;
  const items: UploadState["items"] = [];
  for (const [kind, bytes] of attachments) {
    const counted = uploadKindOf(kind);
    if (counted === null) return null;
    items.push({ kind: counted, bytes, progress: 0 });
  }
  return { channelId, nonce, items };
}

/** The sum of the picked files' sizes. */
export function uploadTotal(upload: UploadState): number {
  return upload.items.reduce((sum, item) => sum + item.bytes, 0);
}

/** How much of the total is behind us: each file's size times its own
 * progress. */
export function uploadDone(upload: UploadState): number {
  const done = upload.items.reduce(
    (sum, item) => sum + Math.floor(item.bytes * clamp01(item.progress)),
    0,
  );
  return Math.min(done, uploadTotal(upload));
}

/** 0..=1 for the bar: by bytes, or — when no size was known — the mean of the
 * items' own progress, so the bar still moves. */
export function uploadFraction(upload: UploadState): number {
  const total = uploadTotal(upload);
  if (total > 0) return clamp01(uploadDone(upload) / total);
  const sum = upload.items.reduce((s, item) => s + clamp01(item.progress), 0);
  return clamp01(sum / Math.max(1, upload.items.length));
}

/** The one kind every attachment travels as, or `null` once two meet. */
export function uploadKind(upload: UploadState): UploadKind | null {
  const first = upload.items[0]?.kind;
  if (first === undefined) return null;
  return upload.items.every((item) => item.kind === first) ? first : null;
}

/** «Отправка 2 фото» — or «Отправка медиа», with no number, when the
 * attachments are of more than one kind (the owner's rule). */
export function uploadTitle(upload: UploadState, t: Dict): string {
  const count = upload.items.length;
  switch (uploadKind(upload)) {
    case "photo":
      return t.uploadPhotos(count);
    case "video":
      return t.uploadVideos(count);
    case "file":
      return t.uploadFiles(count);
    case null:
      return t.uploadMedia();
  }
}

/** The square's glyph: the kind, or a paperclip once two kinds meet. */
export function uploadGlyph(upload: UploadState): string {
  switch (uploadKind(upload)) {
    case "photo":
      return "image";
    case "video":
      return "videocam";
    case "file":
      return "description";
    case null:
      return "attach_file";
  }
}

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}
